import {
  useEffect,
  useLayoutEffect,
  useRef
} from 'react'

import styled from 'styled-components'

import {
  getStoryBackground
} from 'components/UI/BackgroundPicker/BackgroundPicker'
import { StoryText } from 'components-ref/atoms'
import { useGlobalVideo } from 'context/globalVideoContext'
import VideoSurface from 'context/VideoSurface'
import { useCachedStoryImage } from 'helpers/useCachedStoryImage'
import { type UserProfile } from 'models'
import { type Story } from 'types/api/story'
import { type StoryActionType } from 'types/types'

interface StoryMediaProps {
  story: Story
  homeFeed?: boolean
  user: UserProfile
  isPlayerMuted: boolean
  setIsPaused: (x: boolean) => any
  isPaused: boolean
  setAction: (action: StoryActionType) => any
  fullScreen?: boolean
  activeStoryIndex?: any
  onDesktopMediaWidth?: (width: number) => void
  isActive?: boolean
}

export function StoryMedia({
  story,
  homeFeed = false,
  isPlayerMuted,
  isPaused,
  fullScreen = true,
  isActive = true,
  onDesktopMediaWidth
}: StoryMediaProps) {
  const videoUrl = story?.video_src ?? ''
  const mediaId = `${homeFeed ? 'story' : 'story-list'}:${story.id}`
  const ownerId = `story-fullscreen:${mediaId}`

  const isPhoto = Boolean(story.image_src)
  const isVideo = Boolean(story.video_src)

  const photoThumbBlob = useCachedStoryImage(
    isPhoto ? story.image_src_thumb : null
  )

  const photoFullBlob = useCachedStoryImage(
    isPhoto ? story.image_src : null
  )

  const videoThumbBlob = useCachedStoryImage(
    isVideo ? story.video_thumb_src : null
  )

  const mediaContainerRef = useRef<HTMLDivElement>(null)

  const {
    activeMediaId,
    setActiveMediaId,
    play,
    setMuted,
    getVideoElement
  } = useGlobalVideo()

  useLayoutEffect(() => {
    if (homeFeed || !videoUrl || !isActive) return

    const video = getVideoElement(mediaId, videoUrl)
    if (!video) return

    video.currentTime = 0
  }, [
    homeFeed,
    mediaId,
    videoUrl,
    isActive,
    getVideoElement
  ])

  // Claim the existing global video without resetting its source or time.
  useLayoutEffect(() => {
    if (!videoUrl || !isActive) return

    if (activeMediaId !== mediaId) {
      setActiveMediaId(mediaId)
    }
  }, [videoUrl, isActive, mediaId, activeMediaId, setActiveMediaId])

  // Keep the current playback state when taking ownership.
  useEffect(() => {
    if (!videoUrl || !isActive) return
    if (activeMediaId !== mediaId) return

    setMuted(isPlayerMuted)

    const video = getVideoElement(mediaId, videoUrl)
    if (!video) return

    if (isPaused) {

      if (!video.paused) {
        video.pause()
      }

      return
    }
    if (video.ended) {
      video.currentTime = 0
    }

    if (video.paused) {
      void play(mediaId, videoUrl)
    }
  }, [
    videoUrl,
    isActive,
    mediaId,
    activeMediaId,
    isPaused,
    isPlayerMuted,
    play,
    setMuted,
    getVideoElement
  ])

  useEffect(() => {
    if (!onDesktopMediaWidth) return

    const el = mediaContainerRef.current
    if (!el) return

    const updateWidth = () => {
      if (window.innerWidth < 768) return

      const rect = el.getBoundingClientRect()
      onDesktopMediaWidth(rect.width)
    }

    updateWidth()

    const resizeObserver = new ResizeObserver(updateWidth)

    resizeObserver.observe(el)
    window.addEventListener('resize', updateWidth)

    return () => {
      resizeObserver.disconnect()
      window.removeEventListener('resize', updateWidth)
    }
  }, [
    story?.image_src,
    story?.video_src,
    story?.background_color,
    onDesktopMediaWidth
  ])

  return (
    <>
      {Boolean(
        story &&
        !story.image_src &&
        !story.video_src
      ) && (
        <MediaContainer
          ref={mediaContainerRef}
          $fullScreen={fullScreen}
          style={{
            backgroundImage: getStoryBackground(
              story.background_color
            )
          }}
        />
      )}

      {Boolean(story?.image_src) && (
        <MediaBackground
          $fullScreen={fullScreen}
          style={{
            backgroundImage: photoThumbBlob
              ? `url("${photoThumbBlob}")`
              : undefined
          }}
        >
          <MediaContainer
            ref={mediaContainerRef}
            $fullScreen={fullScreen}
            style={{
              backgroundImage: photoFullBlob
                ? `url("${photoFullBlob}")`
                : undefined
            }}
          />
        </MediaBackground>
      )}

      {Boolean(story?.video_src) && (
        <MediaBackground
          ref={mediaContainerRef}
          $fullScreen={fullScreen}
          style={{
            backgroundColor: 'black',
            position: 'relative'
          }}
        >
          <div
            className='preview__background'
            style={{
              backgroundImage: videoThumbBlob
                ? `url("${videoThumbBlob}")`
                : undefined
            }}
          />

          {videoThumbBlob && !isActive && (
            <img
              className='story-video-poster'
              src={videoThumbBlob}
              alt=''
              draggable={false}
            />
          )}

          <VideoSurface
            ownerType='story'
            ownerId={ownerId}
            mediaId={mediaId}
            url={videoUrl}
            cloudfront={story.video_cloudfront}
            poster={videoThumbBlob ?? undefined}
            active={isActive}
            className='story-video-surface'
          />
        </MediaBackground>
      )}

      {Boolean(story?.text?.value) && (
        <TextContainer $fullScreen={fullScreen}>
          <StoryText
            text={story.text?.value ?? ''}
            coordinates={{
              x: story.text?.x ?? 0,
              y: story.text?.y ?? 0
            }}
          />
        </TextContainer>
      )}
    </>
  )
}

interface ContainerProps {
  $fullScreen: boolean
}

const MediaContainer = styled.div<ContainerProps>`
  display: flex;
  flex-direction: column;
  height: 100vh;
  width: 100vw;
  border-radius: inherit;
  align-items: center;
  background-repeat: no-repeat;
  background-size: contain;
  background-position: center;
  backdrop-filter: blur(19px) brightness(0.8);
  -webkit-backdrop-filter: blur(19px) brightness(0.8);

  ${props =>
    props.$fullScreen
      ? ''
      : `
          height: 100%;
          width: 100%;
        `}
`

const MediaBackground = styled.div<ContainerProps>`
  position: relative;
  height: 100vh;
  width: 100vw;
  border-radius: inherit;
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center;
  overflow: hidden;

  .story-video-poster {
    position: absolute;
    inset: 0;
    z-index: 2;
    width: 100%;
    height: 100%;
    object-fit: contain;
    object-position: center;
    pointer-events: none;
    user-select: none;
  }

  .preview__background {
    position: absolute;
    inset: 0;
    z-index: 0;
    width: 100%;
    height: 100%;
    background-size: cover;
    background-position: center;
    filter: blur(24px) brightness(0.7);
    transform: scale(1.08);
    pointer-events: none;
  }

  .story-video-surface {
    position: absolute;
    inset: 0;
    z-index: 1;
    width: 100%;
    height: 100%;
  }

  .story-video-surface video {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: contain;
    object-position: center;
    background: transparent;
  }

  ${props =>
    props.$fullScreen
      ? ''
      : `
          height: 100%;
          width: 100%;
        `}
`

const TextContainer = styled.div<ContainerProps>`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;

  ${props =>
    props.$fullScreen
      ? ''
      : 'transform: scale(0.3);'}
`
